import * as React from 'react';
import { Alert, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Link as RouterLink } from 'react-router-dom';
import { getMatchAiStatus, triggerMatchBrief } from '../../Api';
import { MatchAiStatusDto, MatchBriefScope } from '../../models/dtos/MatchBriefDto';
import MatchBriefDialog, { matchBriefStatusChip } from './MatchBriefDialog';
import MatchBriefExecutionDialog, { MatchBriefStageChip, SCOPE_LABELS } from './MatchBriefExecutionDialog';
import MatchResearchDialog, { matchResearchConfirmedChip } from './MatchResearchDialog';

const APP_STATUS_LABELS: Record<string, string> = {
    ready: 'shown in the app',
    pending: 'being written',
    not_covered: 'not shown',
    after_kickoff: 'kicked off, no longer shown',
};

/** How long the panel keeps checking after a trigger for Crons to take it (it may wait behind others in the queue). */
const QUEUED_POLL_MS = 3 * 60_000;

/**
 * A match's AI on its dashboard page (betup-server-api docs/match-briefs.md §5): its prediction (brief) and its web
 * researches, each readable, and manual triggers for either — a new prediction, or a new research only. Manual triggers
 * skip the scheduler's limits; Crons runs them one at a time.
 */
export default function MatchAiPanel({ matchId, onToast }: { matchId: number; onToast: (message: string) => void }) {
    const [status, setStatus] = React.useState<MatchAiStatusDto | null>(null);
    const [failed, setFailed] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [sending, setSending] = React.useState<MatchBriefScope | null>(null);
    const [pollUntil, setPollUntil] = React.useState(0);
    const [briefOpen, setBriefOpen] = React.useState(false);
    const [researchOpen, setResearchOpen] = React.useState(false);
    const [executionOpen, setExecutionOpen] = React.useState(false);

    const load = React.useCallback(async () => {
        const result = await getMatchAiStatus(matchId);
        setFailed(result == null);
        if (result) setStatus(result);
    }, [matchId]);

    React.useEffect(() => { setStatus(null); load(); }, [load]);

    // Follow an invocation of this match while it runs, and for a while after a trigger until Crons takes it.
    const running = status?.running ?? null;
    React.useEffect(() => {
        if (!running && Date.now() > pollUntil) return;
        const timer = setInterval(() => {
            if (!running && Date.now() > pollUntil) clearInterval(timer);
            load();
        }, 5_000);
        return () => clearInterval(timer);
    }, [running, pollUntil, load]);

    const trigger = async (scope: MatchBriefScope) => {
        setSending(scope);
        setError(null);
        const result = await triggerMatchBrief(matchId, scope);
        setSending(null);
        if (result.error) {
            setError(result.error);
            return;
        }
        onToast(scope === 'RESEARCH' ? 'Queued: a new research of this match' : 'Queued: a new prediction of this match');
        setPollUntil(Date.now() + QUEUED_POLL_MS);
        setTimeout(load, 1500);
    };

    const brief = status?.brief ?? null;
    const latest = status?.latest_research ?? null;
    const busy = running != null || sending != null;

    return (
        <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                    <AutoAwesomeIcon fontSize="small" color="primary" />
                    <Typography variant="h6" sx={{ flex: 1 }}>AI</Typography>
                    <Link component={RouterLink} to={`/matches/ai-predictions?match=${matchId}`} variant="body2">Invocations of this match</Link>
                </Stack>
                {failed && <Alert severity="error" sx={{ mb: 1.5 }}>The match's AI state could not be loaded.</Alert>}
                {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
                {running && (
                    <Alert severity="info" sx={{ mb: 1.5 }} icon={<CircularProgress size={18} />}
                        action={<Button size="small" color="inherit" onClick={() => setExecutionOpen(true)}>Open</Button>}>
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <span>{SCOPE_LABELS[running.scope] ?? running.scope} in progress (invocation #{running.id}):</span>
                            <MatchBriefStageChip status={running.status} />
                        </Stack>
                    </Alert>
                )}
                {!status && !failed && <CircularProgress size={24} />}
                {status && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>Prediction</Typography>
                            {brief ? (
                                <Stack gap={0.75}>
                                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                        {matchBriefStatusChip(brief.status)}
                                        <Typography variant="caption" color="text.secondary">{APP_STATUS_LABELS[brief.app_status] ?? brief.app_status}</Typography>
                                    </Stack>
                                    {brief.headline && <Typography variant="body2">{brief.headline}</Typography>}
                                    <Typography variant="caption" color="text.secondary">
                                        {brief.ready_at ? `Ready ${new Date(brief.ready_at).toLocaleString()}` : 'Not ready yet'}
                                        {brief.research_id ? ` · from research #${brief.research_id}` : ''}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No prediction for this match.</Typography>
                            )}
                            <Stack direction="row" gap={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                                {brief && <Button size="small" variant="outlined" onClick={() => setBriefOpen(true)}>Open prediction</Button>}
                                <Button size="small" variant="contained" startIcon={sending === 'BRIEF' ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                                    disabled={busy} onClick={() => trigger('BRIEF')}>
                                    {brief ? 'Generate again' : 'Generate prediction'}
                                </Button>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Reuses this match's latest research when it is fresh; a ready prediction stays shown until the new one is ready.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Divider sx={{ display: { md: 'none' }, mb: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>Web research</Typography>
                            {latest ? (
                                <Stack gap={0.75}>
                                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                        {matchResearchConfirmedChip(latest.match_confirmed)}
                                        <Chip size="small" variant="outlined" label={`${latest.findings_count} finding${latest.findings_count === 1 ? '' : 's'}`} />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        Latest #{latest.id}, {new Date(latest.researched_at).toLocaleString()} · {status.research_count} in total
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No research for this match.</Typography>
                            )}
                            <Stack direction="row" gap={1} sx={{ mt: 1.5 }} flexWrap="wrap" alignItems="center">
                                {latest && <Button size="small" variant="outlined" onClick={() => setResearchOpen(true)}>Open latest</Button>}
                                <Button size="small" variant="contained" color="secondary"
                                    startIcon={sending === 'RESEARCH' ? <CircularProgress size={14} color="inherit" /> : <TravelExploreIcon />}
                                    disabled={busy} onClick={() => trigger('RESEARCH')}>
                                    Run research
                                </Button>
                                {status.research_count > 1 && (
                                    <Link component={RouterLink} to={`/matches/ai-predictions?tab=researches&match=${matchId}`} variant="body2">
                                        All {status.research_count}
                                    </Link>
                                )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Searches the web again (the prediction stays as it is); the next prediction of this match reuses it.
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </CardContent>
            <MatchBriefDialog briefId={briefOpen && brief ? brief.id : null} onClose={() => setBriefOpen(false)} showMatchLink={false} />
            <MatchResearchDialog researchId={researchOpen && latest ? latest.id : null} onClose={() => setResearchOpen(false)} showMatchLink={false} />
            <MatchBriefExecutionDialog executionId={executionOpen && running ? running.id : null} onClose={() => { setExecutionOpen(false); load(); }} />
        </Card>
    );
}
